import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { postData, getData } from '../../../utils/apiUtils';
import { API_ENDPOINTS } from '../../../config/api.config';
import "./Signup.css";
import snapgradeLogo from '../../../img/snapgrade-logo.png';
import defaultSchool from '../../../img/default-school.png';
import { FiEye, FiEyeOff, FiAlertTriangle } from 'react-icons/fi';
// Import the trial service to generate trial codes
import { generateTrialCode } from '../../../services/trialService';

const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [schoolName, setSchoolName] = useState("");
  const [schoolLogo, setSchoolLogo] = useState("");
  const [schoolPlanType, setSchoolPlanType] = useState(""); // Add plan type state
  const [isSchoolTrial, setIsSchoolTrial] = useState(false); // Add trial state
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    schoolCode: "",
    role: "",
    acceptedLicense: false
  });
  const [schoolCodeError, setSchoolCodeError] = useState("");
  const [schoolCodeParts, setSchoolCodeParts] = useState(['', '', '', '']);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showEULA, setShowEULA] = useState(false);
  const [canAccept, setCanAccept] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const privacyPolicyRef = useRef(null);
  const eulaRef = useRef(null);
  const contentRef = useRef(null);
  
  // Trial related states
  const [isTrial, setIsTrial] = useState(false);
  const [trialStatus, setTrialStatus] = useState(null);
  const [creatingTrial, setCreatingTrial] = useState(false);

  // Check if we have a trial code in the URL or if showTrial is true or if email is pre-filled
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const codeParam = params.get('code');
    const showTrial = params.get('showTrial') === 'true';
    const emailParam = params.get('email');
    
    console.log('URL params:', { codeParam, showTrial, emailParam });
    
    // If email is provided in URL params, use it but allow editing
    if (emailParam) {
      setFormData(prev => ({
        ...prev,
        email: emailParam
      }));
    }
    
    if (codeParam) {
      // If a code parameter is provided, use it
      console.log('Using provided code from URL:', codeParam);
      const codeParts = splitCodeIntoParts(codeParam);
      setSchoolCodeParts(codeParts);
      setFormData(prev => ({ ...prev, schoolCode: codeParam }));
      validateSchoolCode(codeParam);
    } else if (showTrial) {
      // If showTrial is true, we'll set the role to teacher but wait to generate the code
      //console.log('Setting role to teacher for showTrial=true, code will be generated after form completion');
      // Set role to teacher immediately for trial accounts
      setFormData(prev => ({ ...prev, role: "teacher" }));
      setIsTrial(true);
    }
  }, [location]);

  // Function to split a 16-char code into 4 parts of 4 chars each
  const splitCodeIntoParts = (code) => {
    if (!code || code.length !== 16) return ['', '', '', ''];
    return [
      code.substring(0, 4),
      code.substring(4, 8),
      code.substring(8, 12),
      code.substring(12, 16)
    ];
  };

  // Function to generate a trial code and prefill the form
  const generateTrialCodeAndPrefill = async (retryCount = 0, delay = 1000) => {
    try {
      // Generate a new trial code - now this is an async call to the backend
      const trialCode = await generateTrialCode();
      console.log('Generated trial code:', trialCode);
      
      // Split the code into 4 parts
      const codeParts = splitCodeIntoParts(trialCode);
      
      // Update the state
      setSchoolCodeParts(codeParts);
      setFormData(prev => ({ ...prev, schoolCode: trialCode, role: "teacher" }));
      
      // Validate the generated code
      try {
        await validateSchoolCode(trialCode);
        return trialCode; // Return the generated code on success
      } catch (validationError) {
        // Check if it's a rate limit error (429)
        if (validationError.status === 429 && retryCount < 3) {
          console.log(`Rate limit hit, retrying in ${delay}ms (attempt ${retryCount + 1} of 3)`);
          setSchoolCodeError(`Server busy. Retrying in ${delay/1000} seconds... (${retryCount + 1}/3)`);
          
          // Wait for the specified delay
          await new Promise(resolve => setTimeout(resolve, delay));
          
          // Retry with exponential backoff
          return generateTrialCodeAndPrefill(retryCount + 1, delay * 2);
        } else {
          // If we've exhausted retries or it's a different error, use the test code as fallback
          console.error('All retry attempts failed, suggesting test code');
          setSchoolCodeError("Could not generate a trial code. Please try again later or contact support.");
          throw validationError; // Re-throw the error to be caught by the caller
        }
      }
    } catch (error) {
      console.error('Error generating trial code:', error);
      setSchoolCodeError('Failed to generate trial code');
      throw error; // Re-throw the error to be caught by the caller
    } finally {
      setCreatingTrial(false);
    }
  };

  // Function to handle successful registration
  const handleRegistrationSuccess = (schoolCode = null) => {
    setSuccess("User registered successfully");
    
    // Store the school code in localStorage if it's a trial
    if (schoolCode && isTrial) {
      localStorage.setItem('generatedSchoolCode', schoolCode);
      localStorage.setItem('trialSchoolName', schoolName || "SnapGrade Trial School");
      
      // Store success message for login page to display
      localStorage.setItem('registrationSuccess', 'true');
      localStorage.setItem('registrationMessage', 
        "Your account has been created and your school code has been generated. Please log in and go to Settings to view and copy your school code.");
    }
    
    // The useEffect will handle the redirection
    console.log("Registration successful, redirecting in 4 seconds...");
  };

  // Add effect for redirect after successful registration
  useEffect(() => {
    if (success) {
      // Store the school code in localStorage if it's a trial (in case it wasn't stored yet)
      if (isTrial && formData.schoolCode) {
        localStorage.setItem('generatedSchoolCode', formData.schoolCode);
        localStorage.setItem('trialSchoolName', schoolName || "SnapGrade Trial School");
        
        // Store success message for login page to display
        localStorage.setItem('registrationSuccess', 'true');
        localStorage.setItem('registrationMessage', 
          "Your account has been created and your school code has been generated. Please log in and go to Settings to view and copy your school code.");
      }
      
      const timer = setTimeout(() => {
        navigate("/login");
      }, 1500); // Reduced to 1.5 seconds since we'll show message on login page now
      
      // Cleanup function to clear the timeout if component unmounts
      return () => clearTimeout(timer);
    }
  }, [success, navigate, isTrial, formData.schoolCode, schoolName]);

  // Check if this is a "Try for Free" request that needs a trial code
  const needsTrialCodeGeneration = () => {
    const params = new URLSearchParams(location.search);
    return params.get('showTrial') === 'true' && !formData.schoolCode;
  };

  // This function is no longer automatically called from useEffect
  // It will only be called from handleSubmit
  const generateTrialIfFieldsComplete = () => {
    if (needsTrialCodeGeneration() && 
        formData.fullName && 
        formData.email && 
        formData.password && 
        !creatingTrial) {
      setCreatingTrial(true);
      // Set role to teacher immediately for trial accounts
      setFormData(prev => ({ ...prev, role: "teacher" }));
      setTimeout(() => {
        generateTrialCodeAndPrefill();
      }, 500); // Add a small delay to ensure UI updates properly
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Validate password length when it changes
    if (name === 'password') {
      if (value.length > 0 && value.length < 8) {
        setPasswordError("Password must be at least 8 characters long");
      } else {
        setPasswordError("");
      }
      
      // Check if passwords match when password changes
      if (formData.confirmPassword && value !== formData.confirmPassword) {
        setConfirmPasswordError("Passwords do not match");
      } else if (formData.confirmPassword) {
        setConfirmPasswordError("");
      }
    }
    
    // Check if passwords match when confirm password changes
    if (name === 'confirmPassword') {
      if (value !== formData.password) {
        setConfirmPasswordError("Passwords do not match");
      } else {
        setConfirmPasswordError("");
      }
    }
  };

  const validateTrialExpiration = (trialData) => {
    if (!trialData) return false;
    
    // Ensure trial data has an expiration date
    if (!trialData.expirationDate && !trialData.trialExpiresAt) {
      trialData.expirationDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
      console.log('Added missing expiration date:', trialData.expirationDate);
    }
    
    return trialData;
  };

  const validateSchoolCode = async (code) => {
    setIsLoading(true);
    setSchoolCodeError("");
    setSchoolLogo("");
    setIsTrial(false);
    setTrialStatus(null);
    
    // Debug log
    console.log('Validating school code:', code);
    
    try {
      // For newly generated trial codes, we need to create the trial first
      const params = new URLSearchParams(location.search);
      const showTrial = params.get('showTrial') === 'true';
      
      if (showTrial && code && code.length === 16) {
        console.log('Detected showTrial=true with code:', code);
        try {
          // Verify code format before trying to create a trial
          if (!/^[A-Z0-9]{16}$/.test(code)) {
            setSchoolCodeError("Invalid school code format. Code must be 16 characters and contain only uppercase letters and numbers.");
            setIsLoading(false);
            return;
          }
          
          // Create a new trial with the generated code
          console.log('Creating trial with code:', code);
          const trialResult = await postData(API_ENDPOINTS.TRIALS.CREATE, {
            schoolCode: code, // Send the generated code in the expected format
            trialDays: 14 // Specify trial duration
          });
          
          console.log('Trial creation result:', trialResult);
          
          if (trialResult && trialResult.success) {
            setSchoolName("SnapGrade Trial School");
            setSchoolLogo(defaultSchool);
            setIsTrial(true);
            
            // Use the trial status data from the response if available
            if (trialResult.data) {
              console.log('Setting trial status from API response:', trialResult.data);
              // Ensure we have an expiration date
              setTrialStatus(validateTrialExpiration(trialResult.data));
            } else {
              // Fallback status if not provided by the API
              const fallbackTrialData = {
                isTrial: true,
                teacherCount: 0,
                teacherLimit: 1,
                studentCount: 0,
                studentLimit: 1,
                expirationDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString() // 14 days from now
              };
              console.log('Creating fallback trial status with expiration date:', fallbackTrialData.expirationDate);
              setTrialStatus(validateTrialExpiration(fallbackTrialData));
            }
            
            // Set a default message if the trial was created successfully
            if (!schoolName) {
              setSchoolName("SnapGrade Trial School");
            }
            
            // Make sure the form data has the code and role set correctly
            setFormData(prev => ({ 
              ...prev, 
              schoolCode: code, 
              role: "teacher" 
            }));
            
            console.log('Updated form data after trial creation:', formData);
            
            setIsLoading(false);
            return; // Skip the regular validation
          } else {
            console.error('Trial creation failed:', trialResult);
            
            // If we hit a rate limit, throw an error with the status code
            if (trialResult && trialResult.status === 429) {
              const error = new Error("Rate limit exceeded");
              error.status = 429;
              throw error;
            }
            
            setSchoolCodeError(trialResult?.message || "Failed to create trial");
          }
        } catch (trialError) {
          console.error('Error creating trial:', trialError);
          // If it's a rate limit error, propagate it to allow retry logic
          if (trialError.status === 429) {
            throw trialError;
          }
          setSchoolCodeError("Failed to create trial: " + (trialError.message || ''));
        }
      }
      
      // Regular school code validation
      try {
        console.log('Performing regular validation for code:', code);
        // First, validate the school code
        const data = await postData(
          '/api/v1/stem@home/auth/validate-school',
          { schoolCode: code }
        );
        
        console.log('School validation response:', data);

        if (data && data.isValid) {
          setSchoolName(data.schoolName);
          setSchoolPlanType(data.planType || 'personal');
          setIsSchoolTrial(data.isTrial || false);
          
          // Only set school logo for Institution plans, otherwise use default
          if (data.planType === 'institution') {
            setSchoolLogo(defaultSchool); // Could be enhanced to use actual school logo if available
          } else {
            setSchoolLogo("");
          }
          
          // If it's a trial from the validation response, handle trial logic
          if (data.isTrial) {
            // Now, check if this is a trial school for detailed trial info
            try {
              console.log('Checking trial details for school:', code);
              const trialResponse = await getData(API_ENDPOINTS.TRIALS.SCHOOL_STATUS(code));
              
              console.log('Trial status API response:', trialResponse);
              if (trialResponse && trialResponse.data) {
                console.log('Trial status data:', trialResponse.data);
                console.log('Expiration field in response:', {
                  expirationDate: trialResponse.data.expirationDate,
                  trialExpiresAt: trialResponse.data.trialExpiresAt
                });
              }
              
              if (trialResponse && trialResponse.success && trialResponse.data && trialResponse.data.isTrial) {
                setIsTrial(true);
                console.log('Setting trial status data:', trialResponse.data);
                
                setTrialStatus(validateTrialExpiration(trialResponse.data));
                
                console.log('Expiration date in response:', trialResponse.data.expirationDate || trialResponse.data.trialExpiresAt);
                setTrialStatus(trialResponse.data);
                
                // Validate trial account creation based on role
                if (formData.role === 'teacher' && trialResponse.data.teacherCount >= trialResponse.data.teacherLimit) {
                  setSchoolCodeError("This trial has reached the teacher limit. Only 1 teacher account is allowed.");
                } else if (formData.role === 'student' && trialResponse.data.studentCount >= trialResponse.data.studentLimit) {
                  setSchoolCodeError("This trial has reached the student limit. Only 1 student account is allowed.");
                }
                
                // Check trial expiration
                const now = new Date();
                const expiration = new Date(trialResponse.data.expirationDate);
                if (now > expiration) {
                  setSchoolCodeError("This trial has expired. Please upgrade to a full plan.");
                }
              }
            } catch (trialError) {
              console.error('Error checking trial status:', trialError);
              // Don't show an error to the user if we can't check trial status
              // The school code is still valid
            }
          } else {
            console.log('This is not a trial school');
            setIsTrial(false);
          }
        } else {
          setSchoolName("");
          setSchoolPlanType("");
          setIsSchoolTrial(false);
          // Try using the test code provided by the backend developer if validation fails
          if (code === "TEST1234ABCDEFGH") {
            setSchoolName("SnapGrade Test Trial School");
            setSchoolLogo(defaultSchool);
            setSchoolPlanType("personal");
            setIsSchoolTrial(true);
            setIsTrial(true);
            const testTrialData = {
              isTrial: true,
              teacherCount: 0,
              teacherLimit: 1,
              studentCount: 0,
              studentLimit: 1,
              expirationDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString()
            };
            console.log('Creating test trial with expiration date:', testTrialData.expirationDate);
            setTrialStatus(validateTrialExpiration(testTrialData));
          } else {
            setSchoolCodeError(data?.message || "Invalid school code");
          }
        }
      } catch (error) {
        console.error('School code validation error:', error);
        setSchoolName("");
        setSchoolPlanType("");
        setIsSchoolTrial(false);
        setSchoolCodeError("Failed to validate school code");
      }
    } catch (error) {
      console.error('Overall validation error:', error);
      setSchoolName("");
      setSchoolPlanType("");
      setIsSchoolTrial(false);
      setSchoolCodeError("Failed to validate school code: " + (error.message || ''));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSchoolCodePartChange = (e, index) => {
    // Clean the input value - only allow letters and numbers
    const cleanedValue = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
    
    console.log(`Updating school code part ${index} to:`, cleanedValue);
    
    // Update the specific part of the school code
    const newParts = [...schoolCodeParts];
    newParts[index] = cleanedValue;
    setSchoolCodeParts(newParts);
    
    // Update the full school code
    const fullCode = newParts.join('');
    console.log('Full school code updated to:', fullCode);
    
    setFormData(prevData => ({
      ...prevData,
      schoolCode: fullCode
    }));
    
    // Auto-focus to next input if current part is filled
    if (cleanedValue.length === 4 && index < 3) {
      const nextInput = document.querySelector(`input[name="schoolCode${index + 1}"]`);
      if (nextInput) nextInput.focus();
    }
    
    // Validate the code if it's complete
    if (fullCode.length === 16) {
      console.log('School code complete, validating:', fullCode);
      validateSchoolCode(fullCode);
    } else {
      setSchoolName("");
      setSchoolPlanType("");
      setIsSchoolTrial(false);
      if (fullCode.length > 0) {
        setSchoolCodeError("School code must be 16 characters");
      }
    }
  };

  // Function to fill in the test code
  const fillTestCode = () => {
    const testCode = "TEST1234ABCDEFGH";
    const codeParts = splitCodeIntoParts(testCode);
    setSchoolCodeParts(codeParts);
    setFormData(prevData => ({
      ...prevData,
      schoolCode: testCode,
      role: "teacher" // Default to teacher for test code
    }));
    validateSchoolCode(testCode);
  };

  // Handle school code key down events
  const handleSchoolCodeKeyDown = (e, index) => {
    // Handle backspace
    if (e.key === 'Backspace' && !schoolCodeParts[index] && index > 0) {
      const prevInput = document.querySelector(`input[name="schoolCode${index - 1}"]`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleScroll = (e) => {
    const element = e.target;
    
    // Calculate the percentage scrolled (0-100)
    const scrollPercentage = 
      (element.scrollTop / (element.scrollHeight - element.clientHeight)) * 100;
    
    // Enable the accept button after just 20% scrolling to make it easier for users
    if (scrollPercentage >= 20) {
      setCanAccept(true);
    }
    
    // Also enable if the user scrolls more than 300px
    if (element.scrollTop > 300) {
      setCanAccept(true);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit();
  };

  const handlePrivacyPolicyAccept = () => {
    setShowPrivacyPolicy(false);
    setShowEULA(true);
    setCanAccept(false); // Reset for EULA
  };

  const handleEULAAccept = () => {
    setFormData(prev => ({ ...prev, acceptedLicense: true }));
    setShowEULA(false);
  };

  const handlePrivacyPolicyDecline = () => {
    setShowPrivacyPolicy(false);
    setShowEULA(false);
    setFormData(prev => ({ ...prev, acceptedLicense: false }));
  };

  const handlePrivacyCheckboxClick = (e) => {
    if (!formData.acceptedLicense) {
      e.preventDefault();
      setShowPrivacyPolicy(true);
      setShowEULA(false);
      setCanAccept(false);
    }
  };

  const handleSubmit = async (e) => {
    if (!formData.acceptedLicense) {
      setError("Please accept the Privacy Policy and End User Licence Agreement to continue.");
      return;
    }

    // Validate password length before submission
    if (formData.password.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      setError("Please fix the errors before submitting.");
      return;
    }
    
    // Validate passwords match before submission
    if (formData.password !== formData.confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      setError("Please make sure your passwords match before submitting.");
      return;
    }

    setError("");
    setSuccess("");
    setPasswordError(""); // Clear password error on valid submission
    setConfirmPasswordError(""); // Clear confirm password error on valid submission

    // If this is a trial sign-up but we don't have a school code yet,
    if (needsTrialCodeGeneration()) {
      if (!formData.fullName || !formData.email || !formData.password) {
        setError("Please fill in all required fields");
        return;
      }
      
      console.log('Trial signup - starting registration for user:', {
        fullName: formData.fullName,
        email: formData.email,
        role: 'teacher'
      });
      
      setIsLoading(true);
      setCreatingTrial(true);
      
      try {
        // Create the trial on the backend with required user information
        console.log('Creating trial with user info');
        const trialResult = await postData(API_ENDPOINTS.TRIALS.CREATE, {
          email: formData.email.toLowerCase(),
          name: formData.fullName,
          trialDays: 14
        });
        
        if (!trialResult || !trialResult.success) {
          throw new Error(trialResult?.message || "Failed to create trial code");
        }
        
        const trialCode = trialResult.data?.schoolCode;
        
        if (!trialCode) {
          throw new Error("No school code returned from trial creation");
        }
        
        console.log('Trial creation successful, received code:', trialCode);
        
        // Now, register the user with the newly created and validated code
        const registrationData = {
          fullName: formData.fullName,
          email: formData.email.toLowerCase(), // Convert email to lowercase
          password: formData.password,
          schoolCode: trialCode,
          role: "teacher",
          name: formData.fullName
        };
        
        console.log('Attempting to register trial user with data:', registrationData);
        
        // Try to register with automatic retry only for email-related errors
        const tryRegisterWithRetry = async (maxRetries = 3) => {
          for (let attempt = 0; attempt < maxRetries; attempt++) {
            try {
              // Register the user
              const response = await postData(
                '/api/v1/stem@home/auth/register', 
                registrationData
              );
              
              console.log(`Registration attempt ${attempt + 1} response:`, response);
              
              // If successful or error is not about email, return response
              if (response.success || !response.message.includes("email")) {
                return response;
              }
              
              // If we get here, it means the email exists - ask the user to try another email
              console.log(`Email already exists (attempt ${attempt + 1}/${maxRetries}).`);
              return response;
              
            } catch (error) {
              // For network errors or other exceptions, just return an error response
              console.error(`Registration attempt ${attempt + 1} failed with error:`, error);
              return { 
                success: false, 
                message: error.response?.data?.message || error.message || "Registration failed" 
              };
            }
          }
          
          // If we've exhausted all retries
          return { success: false, message: "Failed to register after multiple attempts" };
        };
        
        // Execute the registration
        const response = await tryRegisterWithRetry();
        
        // Only if registration is successful, save the trial code and confirm its creation
        if (response.success === true) {
          console.log('Trial registration successful, using code:', trialCode);
          
          // Store the code and show success message
          handleRegistrationSuccess(trialCode);
          
          // Modified success message that will be shown on the login page
          setSuccess("Your account has been created. Redirecting to login page...");
          
          // Store message for login page
          localStorage.setItem('registrationSuccess', 'true');
          localStorage.setItem('registrationMessage', 
            "Your account has been created and your school code has been generated. Please log in and go to Settings to view and copy your school code.");
          
          // Set form data with the confirmed code
          setFormData(prev => ({ ...prev, schoolCode: trialCode }));
          
          console.log('Trial creation confirmed for:', {
            user: registrationData.fullName,
            email: registrationData.email,
            schoolCode: trialCode
          });
        } else {
          // Registration failed - clear any temporary data
          setFormData(prev => ({ ...prev, schoolCode: "" }));
          setSchoolCodeParts(['', '', '', '']);
          // Clear all school information when registration fails
          setSchoolName("");
          setSchoolLogo("");
          setSchoolPlanType("");
          setIsSchoolTrial(false);
          setIsTrial(false);
          setTrialStatus(null);
          // Display the exact error message from the API
          setError(response.message || "Registration failed");
          console.error('Registration failed:', response);
        }
      } catch (error) {
        // Error occurred - clear any temporary data
        setFormData(prev => ({ ...prev, schoolCode: "" }));
        setSchoolCodeParts(['', '', '', '']);
        // Clear all school information when registration fails
        setSchoolName("");
        setSchoolLogo("");
        setSchoolPlanType("");
        setIsSchoolTrial(false);
        setIsTrial(false);
        setTrialStatus(null);
        
        console.error('Error during trial registration:', error);
        
        // Display specific error message if available
        if (error.response) {
          if (error.response.data && error.response.data.message) {
            setError(error.response.data.message);
          } else if (error.response.status === 400) {
            setError("Invalid registration information. Please check your details and try again.");
          } else if (error.response.status === 409) {
            setError("This email address is already in use. Please use a different email.");
          } else if (error.response.status === 500) {
            setError("Server error. Please try again later or contact support.");
          } else {
            setError(`Registration failed: ${error.response.status}`);
          }
        } else if (error.request) {
          setError("No response from server. Please check your internet connection and try again.");
        } else {
          setError(error.message || "An error occurred during registration. Please try again.");
        }
      } finally {
        setIsLoading(false);
        setCreatingTrial(false);
      }
      
      return;
    }
    
    // Regular (non-trial) registration flow continues here
    // Validate school code
    if (!formData.schoolCode) {
      setError("Please enter a valid school code");
      return;
    }

    setIsLoading(true);

    try {
      const registrationData = {
        fullName: formData.fullName,
        email: formData.email.toLowerCase(), // Convert email to lowercase
        password: formData.password,
        schoolCode: formData.schoolCode,
        role: formData.role,
        name: formData.fullName
      };
      
      const response = await postData(
        '/api/v1/stem@home/auth/register', 
        registrationData
      );

      // Check for success in the response
      if (response.success === true) {
        // Use the message from the server response if available
        if (response.message) {
          setSuccess(response.message);
        } else {
          handleRegistrationSuccess(formData.schoolCode);
        }
      } else {
        // Display the exact error message from the API
        setError(response.message || "Registration failed");
        console.error('Registration failed:', response);
        
        // Clear all school information when registration fails
        setSchoolName("");
        setSchoolLogo("");
        setIsTrial(false);
        setTrialStatus(null);
      }
    } catch (error) {
      // Display specific error message if available
      console.error('Registration error:', error);
      
      // Clear all school information when registration fails
      setSchoolName("");
      setSchoolLogo("");
      setSchoolPlanType("");
      setIsSchoolTrial(false);
      setIsTrial(false);
      setTrialStatus(null);
      
      // Improved error handling to display user-friendly messages
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.data && error.response.data.message) {
          setError(error.response.data.message);
        } else if (error.response.status === 400) {
          setError("Invalid registration information. Please check your details and try again.");
        } else if (error.response.status === 401) {
          setError("Authentication failed. Please check your credentials.");
        } else if (error.response.status === 409) {
          setError("This email address is already in use. Please use a different email.");
        } else if (error.response.status === 500) {
          setError("Server error. Please try again later or contact support.");
        } else {
          setError(`Registration failed: ${error.response.status}`);
        }
        console.error('Error response:', error.response.data);
      } else if (error.request) {
        // The request was made but no response was received
        setError("No response from server. Please check your internet connection and try again.");
        console.error('Error request:', error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("An error occurred during registration. Please try again.");
        console.error('Error message:', error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="left-section">
        <div className="logo-container">
          <img 
            src={snapgradeLogo} 
            alt="SnapGrade" 
            className="logo" 
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer' }}
          />
          {/* School Info Display Box - Only show for Institution plans */}
          {(schoolName && schoolPlanType === 'institution') && (
            <div className="school-info-box">
              <div className="school-logo-container">
                <img 
                  src={schoolLogo || defaultSchool} 
                  alt={schoolName || "School Logo"} 
                  className="school-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = defaultSchool;
                  }}
                />
              </div>
              <div className="school-text-container">
                <div className="school-info-heading">
                  <h3>{schoolName || "SnapGrade School"}</h3>
                  {isTrial && <span className="trial-badge">TRIAL</span>}
                </div>
                {isTrial && trialStatus && (
                  <div className="trial-info">
                    <h2>Trial Account</h2>
                    <p>Limited access for 14 days</p>
                    <div className="account-limits">
                      <p>1 teacher account</p>
                      <p>1 student account</p>
                    </div>
                    <p className="trial-expiry">
                      Expires: {trialStatus && (trialStatus.expirationDate || trialStatus.trialExpiresAt) ? 
                        new Date(trialStatus.expirationDate || trialStatus.trialExpiresAt).toLocaleDateString() : 
                        new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="right-section">
        <div className="signup-form-container">
          <div className="header-section">
            <h1 className="signup-title">Create an account</h1>
            <p className="subtitle">Join SnapGrade to start your learning journey</p>
          </div>

          {isTrial && (
            <div className="trial-info-box">
              <h3>Free Plan Account Information</h3>
              <p>You are registering for a free plan account which includes:</p>
              <ul>
                <li>1 teacher account</li>
                <li>Up to 3 students</li>
                <li>2 classes maximum</li>
                <li>10 submissions</li>
              </ul>
            </div>
          )}

          {/* Add a trial-specific note for users coming from Trial button */}
          {needsTrialCodeGeneration() && (
            <div className="trial-note-box mb-4">
              <p><strong>You're creating a free plan account!</strong></p>
              <p>Fill in your details and click "Create Account" to generate your school code automatically. You don't need to provide one.</p>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <form onSubmit={handleFormSubmit}>
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                name="fullName"
                placeholder="Enter your full name"
                value={formData.fullName}
                onChange={handleInputChange}
                className="form-control"
                required
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
                className={`form-control ${location.search.includes('email=') ? 'verified-email' : ''}`}
                readOnly={location.search.includes('email=')}
                required
              />
            </div>

            <div className="form-group">
              <label>Password</label>
              <div className="password-input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Create a password (min. 8 characters)"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`form-control ${passwordError ? 'error-input' : ''}`}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {passwordError && <div className="error-message">{passwordError}</div>}
            </div>

            <div className="form-group">
              <label>Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={`form-control ${confirmPasswordError ? 'error-input' : ''}`}
                required
              />
              {confirmPasswordError && <div className="error-message">{confirmPasswordError}</div>}
            </div>

            {/* Only show school code input if NOT coming from Try for Free, or if code is already generated */}
            {!needsTrialCodeGeneration() && (
              <div className="form-group">
                <label>School Code</label>
                
                {creatingTrial ? (
                  <div className="note mt-1 mb-2">
                    <span className="waiting-message">Generating your trial code...</span>
                  </div>
                ) : (
                  <>
                    <div className="note mt-1 mb-2">
                      <span>Enter your school code below</span>
                    </div>
                    {schoolCodeError && (
                      <div className="error-message mb-2">{schoolCodeError}</div>
                    )}
                    {isTrial && formData.schoolCode && (
                      <div className="school-code-note mb-2">
                        <strong>Important:</strong> Please keep note of your school code. You'll need it to connect additional accounts to the same school.
                      </div>
                    )}
                    <div className="school-code-inputs">
                      {schoolCodeParts.map((part, index) => (
                        <input
                          key={index}
                          type="text"
                          name={`schoolCode${index}`}
                          value={part}
                          onChange={(e) => handleSchoolCodePartChange(e, index)}
                          onKeyDown={(e) => handleSchoolCodeKeyDown(e, index)}
                          maxLength={4}
                          className="school-code-part"
                          placeholder="XXXX"
                          disabled={creatingTrial}
                          required={!needsTrialCodeGeneration()}
                        />
                      ))}
                    </div>
                    {/* Show "Joining: [School Name]" message for valid schools */}
                    {schoolName && !schoolCodeError && (
                      <div style={{
                        marginTop: '10px',
                        color: '#22c55e',
                        fontSize: '14px',
                        fontWeight: '500',
                        textAlign: 'center'
                      }}>
                        Joining: {schoolName}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="privacy-policy-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.acceptedLicense}
                  onClick={(e) => {
                    if (!formData.acceptedLicense) {
                      e.preventDefault();
                      setShowPrivacyPolicy(true);
                      setShowEULA(false);
                      setCanAccept(false);
                    }
                  }}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      acceptedLicense: e.target.checked
                    });
                  }}
                />
                <div className="checkbox-text">
                  <span>I have read and accept:</span>
                  <div className="policy-links">
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                    <a href="/eula" target="_blank" rel="noopener noreferrer">End User Licence Agreement</a>
                  </div>
                </div>
              </label>
            </div>

            {/* Privacy Policy Modal - Replace with the EXACT same content as in PrivacyPolicy.jsx */}
            {showPrivacyPolicy && (
              <div className="modal-overlay">
                <div className="privacy-policy-modal">
                  <div className="modal-header">
                    <h2>Privacy Policy</h2>
                  </div>
                  <div className="modal-content" onScroll={handleScroll} ref={privacyPolicyRef}>
                    <div className="privacy-policy-section">
                      <h1>SnapGrade Customer Privacy Notice</h1>
                      <p className="mb-2"><strong>Registered name:</strong> STEM@Home Ltd</p>
                      <p className="mb-5">This privacy notice tells you what to expect us to do with your personal information.</p>
                      
                      <div className="privacy-content">
                        <h2>Contact details</h2>
                        <p><strong>Email:</strong> <a href="mailto:francis.jones@snapgrade.ai">francis.jones@snapgrade.ai</a></p>
                        
                        <h2 className="mt-5">What information we collect, use, and why</h2>
                        <p>We collect or use the following information to provide and improve products and services for clients:</p>
                        <ul>
                          <li>Names and contact details</li>
                          <li>Payment details (including card or bank information for transfers and direct debits)</li>
                          <li>Usage data (including information about how you interact with and use our website, products and services)</li>
                        </ul>
                        
                        <p className="mt-4">We collect or use the following personal information for the operation of client or customer accounts:</p>
                        <ul>
                          <li>Names and contact details</li>
                          <li>Purchase or service history</li>
                        </ul>
                        
                        <h2 className="mt-5">Information Collected</h2>
                        <p>When you register for SnapGrade, we collect certain personally identifiable information, which may include:</p>
                        <ul>
                          <li>Name</li>
                          <li>Email address</li>
                          <li>Educational institution information</li>
                          <li>Account login credentials</li>
                        </ul>
                        
                        <p className="mt-4">When you use our Service to scan and submit work for grading, we process:</p>
                        <ul>
                          <li>Images of handwritten or typed work that you upload</li>
                          <li>Text extracted from those images</li>
                          <li>Grading and feedback data generated by our system</li>
                        </ul>
                        
                        <div className="p-4 my-4 bg-light rounded">
                          <p className="mb-0"><strong>IMPORTANT:</strong> We do not store or retain the images you upload to our Service. Once work is scanned and text is extracted, the image is deleted immediately. We cannot access these images after processing. We only save the extracted text, scores, and feedback for each submission.</p>
                        </div>
                        
                        <p className="mt-4">We also automatically gather certain information about your use of the Platform, such as:</p>
                        <ul>
                          <li>Device information (type, operating system, browser)</li>
                          <li>IP address</li>
                          <li>Usage patterns and frequency</li>
                          <li>Log data</li>
                          <li>Cookies and similar technologies</li>
                        </ul>
                        
                        <p className="mt-4">Most browsers can be set to prevent cookies or to notify you when one is placed. However, cookies may be necessary to access some interactive features of our Platform.</p>
                        
                        <h2 className="mt-5">How We Use Your Information</h2>
                        <p>We use your personal information to:</p>
                        <ul>
                          <li>Provide, maintain, and improve the SnapGrade Service</li>
                          <li>Process and complete transactions</li>
                          <li>Send administrative information, including confirmations, technical notices, updates, and security alerts</li>
                          <li>Respond to your comments, questions, and requests</li>
                          <li>Provide customer service and support</li>
                          <li>Monitor and analyze trends, usage, and activities in connection with our Service</li>
                          <li>Detect, prevent, and address technical issues</li>
                          <li>Comply with legal obligations</li>
                        </ul>
                        
                        <p>We will not share or disclose your information to anyone except as described in this Privacy Policy.</p>
                        
                        <h2 className="mt-5">Data Processing and Retention</h2>
                        <p>When you upload images to our Service:</p>
                        <ul>
                          <li>The image is temporarily stored on our secure servers for immediate processing</li>
                          <li>Our system extracts the text content from the image</li>
                          <li>The extracted text is analyzed for grading purposes</li>
                          <li>Feedback and scores are generated and saved to your account</li>
                          <li>The original image is permanently deleted from our servers immediately after processing</li>
                        </ul>
                        
                        <p className="mt-4">We retain only:</p>
                        <ul>
                          <li>Your account information</li>
                          <li>The extracted text from submissions</li>
                          <li>Scores and feedback for each submission</li>
                          <li>Analytics data related to your usage of the Service</li>
                        </ul>
                        
                        <h2 className="mt-5">Sharing Your Information</h2>
                        <p>We do not sell, rent, or share your personal information with third parties for their marketing purposes without your express permission.</p>
                        
                        <p className="mt-4">In limited circumstances, we may share your information with:</p>
                        <ul>
                          <li>Service providers who perform services on our behalf (e.g., cloud hosting, data analysis, payment processing)</li>
                          <li>Educational institutions with which you are affiliated, if you are using SnapGrade through such an institution</li>
                          <li>As required by law, such as to comply with a subpoena or similar legal process when we believe in good faith that disclosure is necessary to protect our rights, protect your safety or the safety of others, investigate fraud, or respond to a government request</li>
                        </ul>
                        
                        <p className="mt-4">In the event of a merger, acquisition, or sale of all or a portion of our assets, users will be notified via email and/or a prominent notice on our website of any change in ownership or uses of your personal information.</p>
                        
                        <h2 className="mt-5">Data Security</h2>
                        <p>We implement appropriate technical and organizational measures to protect the security of your personal information. However, please be aware that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.</p>
                        
                        <p className="mt-4">Should we learn that a personal information breach has occurred, we will:</p>
                        <ul>
                          <li>Take immediate steps to contain and recover from the breach</li>
                          <li>Assess the severity of the breach</li>
                          <li>Notify affected users and relevant regulatory authorities (such as the Information Commissioner's Office) when required by law, without undue delay</li>
                        </ul>
                        
                        <h2 className="mt-5">Payment Processing</h2>
                        <p>If our Service requires payment, we use industry-standard third-party payment processors to handle all financial transactions. We do not store or process your complete payment information on our servers. All payment data is handled securely by our payment processors in compliance with applicable data security standards.</p>
                        
                        <h2 className="mt-5">Lawful bases and data protection rights</h2>
                        <p>Under UK data protection law, we must have a "lawful basis" for collecting and using your personal information. There is a list of possible lawful bases in the UK GDPR. You can find out more about lawful bases on the ICO's website.</p>
                        
                        <p className="mt-4">Which lawful basis we rely on may affect your data protection rights which are in brief set out below. You can find out more about your data protection rights and the exemptions which may apply on the ICO's website:</p>
                        <ul>
                          <li><strong>Your right of access</strong> - You have the right to ask us for copies of your personal information. You can request other information such as details about where we get personal information from and who we share personal information with. There are some exemptions which means you may not receive all the information you ask for.</li>
                          <li><strong>Your right to rectification</strong> - You have the right to ask us to correct or delete personal information you think is inaccurate or incomplete.</li>
                          <li><strong>Your right to erasure</strong> - You have the right to ask us to delete your personal information.</li>
                          <li><strong>Your right to restriction of processing</strong> - You have the right to ask us to limit how we can use your personal information.</li>
                          <li><strong>Your right to object to processing</strong> - You have the right to object to the processing of your personal data.</li>
                          <li><strong>Your right to data portability</strong> - You have the right to ask that we transfer the personal information you gave us to another organisation, or to you.</li>
                          <li><strong>Your right to withdraw consent</strong> - When we use consent as our lawful basis you have the right to withdraw your consent at any time.</li>
                        </ul>
                        
                        <p className="mt-4">If you make a request, we must respond to you without undue delay and in any event within one month.</p>
                        <p>To make a data protection rights request, please contact us using the contact details at the top of this privacy notice.</p>
                        
                        <h2 className="mt-5">Our lawful bases for the collection and use of your data</h2>
                        <p>Our lawful bases for collecting or using personal information to provide and improve products and services for clients are:</p>
                        <ul>
                          <li><strong>Consent</strong> - we have permission from you after we gave you all the relevant information. All of your data protection rights may apply, except the right to object. To be clear, you do have the right to withdraw your consent at any time.</li>
                        </ul>
                        
                        <h2 className="mt-5">Where we get personal information from</h2>
                        <ul>
                          <li>Directly from you</li>
                        </ul>
                        
                        <h2 className="mt-5">How long we keep information</h2>
                        <p>This Data Retention Schedule establishes the guidelines for retaining and disposing of data collected and processed by SnapGrade in accordance with our Privacy Policy. This schedule is designed to ensure that we maintain data only as long as necessary for our legitimate business purposes and to comply with applicable laws and regulations.</p>
                        
                        <div className="table-responsive mt-4">
                          <h3 className="text-center mb-3">DATA CATEGORIES AND RETENTION PERIODS</h3>
                          <table className="table table-bordered">
                            <thead className="bg-light">
                              <tr>
                                <th className="fw-bold">Data Type</th>
                                <th className="fw-bold">Description</th>
                                <th className="fw-bold">Retention Period</th>
                                <th className="fw-bold">Retention Justification</th>
                                <th className="fw-bold">Disposal Method</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="fw-bold">User Account Information</td>
                                <td>Name, email address, educational institution, login credentials</td>
                                <td>Active period of account + 24 months after account inactivity</td>
                                <td>Necessary for account management and potential reactivation</td>
                                <td>Secure deletion from databases</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Uploaded Images</td>
                                <td>Photos of handwritten or typed work submitted for grading</td>
                                <td>Immediate deletion after text extraction (typically within minutes of upload)</td>
                                <td>No business need to retain images once text is extracted</td>
                                <td>Immediate secure deletion</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Extracted Text</td>
                                <td>Text content extracted from uploaded images</td>
                                <td>24 months from date of submission</td>
                                <td>Necessary for reference, appeals, and educational progress tracking</td>
                                <td>Secure deletion from databases</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Scores and Feedback</td>
                                <td>Assessment results and comments generated by the system</td>
                                <td>36 months from date of submission</td>
                                <td>Necessary for educational progress tracking and historical performance analysis</td>
                                <td>Secure deletion from databases</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">User Activity Logs</td>
                                <td>Records of platform usage including login times, feature usage</td>
                                <td>12 months from creation</td>
                                <td>Security monitoring, service improvement, and troubleshooting</td>
                                <td>Secure deletion from databases</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Communication Records</td>
                                <td>Support tickets, emails, and other communications with users</td>
                                <td>24 months from last communication</td>
                                <td>Customer support reference and quality assurance</td>
                                <td>Secure deletion from databases</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Payment Information</td>
                                <td>Transaction records (not full payment details)</td>
                                <td>7 years from transaction date</td>
                                <td>Financial record-keeping, refund processing, and tax compliance</td>
                                <td>Secure deletion from databases</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Application Logs</td>
                                <td>System logs, error reports</td>
                                <td>90 days from creation</td>
                                <td>Troubleshooting and system maintenance</td>
                                <td>Automatic deletion</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Analytics Data</td>
                                <td>Anonymized usage statistics</td>
                                <td>36 months from creation</td>
                                <td>Service improvement and feature development</td>
                                <td>Conversion to fully anonymized aggregate data after this period</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        
                        <h3 className="mt-4">Special Circumstances</h3>
                        <h4>Academic Institution Requests</h4>
                        <p>For educational institutions using SnapGrade as part of their official curriculum:</p>
                        <ul>
                          <li>Upon written request, we may retain specific student data for periods aligned with the institution's academic record retention requirements</li>
                          <li>Such extended retention must be formally documented with the requesting institution</li>
                        </ul>
                        
                        <h4 className="mt-3">Legal Holds</h4>
                        <ul>
                          <li>Data subject to legal holds or preservation notices will be retained until the hold is lifted, regardless of the scheduled retention period</li>
                          <li>Legal department approval is required to implement or remove legal holds</li>
                        </ul>
                        
                        <h4 className="mt-3">User Deletion Requests</h4>
                        <ul>
                          <li>User requests for data deletion will be honored within 30 days as required by applicable law</li>
                          <li>Exceptions apply only where retention is required by law or legitimate business purposes as permitted by applicable regulations</li>
                        </ul>
                        
                        <h3 className="mt-4">Retention Enforcement</h3>
                        <ul>
                          <li><strong>Automated Enforcement:</strong> Where technically feasible, the system automatically enforces retention periods through scheduled deletion processes</li>
                          <li><strong>Manual Review:</strong> Semi-annual review of data repositories to identify and properly dispose of data beyond retention periods</li>
                          <li><strong>Verification:</strong> Annual audit of retention practices to ensure compliance with this schedule</li>
                        </ul>
                        
                        <h2 className="mt-5">Who we share information with</h2>
                        <h3>Data processors</h3>
                        <p><strong>AWS (Amazon Web Services)</strong></p>
                        <p>This data processor does the following activities for us: When you upload images to our Service: The image is temporarily stored on our secure servers for immediate processing Our system extracts the text content from the image The extracted text is analyzed for grading purposes Feedback and scores are generated and saved to your account The original image is permanently deleted from our servers immediately after processing</p>
                        
                        <h2 className="mt-5">Sharing information outside the UK</h2>
                        <p>Where necessary, we may transfer personal information outside of the UK. When doing so, we comply with the UK GDPR, making sure appropriate safeguards are in place.</p>
                        <p>For further information or to obtain a copy of the appropriate safeguard for any of the transfers below, please contact us using the contact information provided above.</p>
                        
                        <ul className="mt-4">
                          <li><strong>Organisation name:</strong> AWS (Amazon Web Services)</li>
                          <li><strong>Category of recipient:</strong> Cloud Service</li>
                          <li><strong>Country the personal information is sent to:</strong> United States of America</li>
                          <li><strong>How the transfer complies with UK data protection law:</strong> The country or sector has a UK data bridge (also known as Adequacy Regulations)</li>
                        </ul>
                        
                        <h2 className="mt-5">How to complain</h2>
                        <p>If you have any concerns about our use of your personal data, you can make a complaint to us using the contact details at the top of this privacy notice.</p>
                        <p>If you remain unhappy with how we've used your data after raising a complaint with us, you can also complain to the ICO.</p>
                        
                        <p className="mt-4">The ICO's address:</p>
                        <address>
                          Information Commissioner's Office<br />
                          Wycliffe House<br />
                          Water Lane<br />
                          Wilmslow<br />
                          Cheshire<br />
                          SK9 5AF
                        </address>
                        
                        <p className="mt-4"><strong>Helpline number:</strong> 0303 123 1113</p>
                        <p><strong>Website:</strong> <a href="https://www.ico.org.uk/make-a-complaint" target="_blank" rel="noopener noreferrer">https://www.ico.org.uk/make-a-complaint</a></p>
                        
                        <div className="mt-5 pt-3 border-top">
                          <p><strong>Last updated:</strong> 11th March 2025</p>
                        </div>
                        
                        {/* Add spacer to ensure scrolling works properly */}
                        <div style={{ height: "120px", width: "100%" }}></div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-actions">
                    <button onClick={handlePrivacyPolicyDecline} className="btn-decline">
                      Decline
                    </button>
                    <button 
                      onClick={handlePrivacyPolicyAccept} 
                      className="btn-accept"
                      disabled={!canAccept}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* EULA Modal - Replace with the EXACT same content as in EULA.jsx */}
            {showEULA && (
              <div className="modal-overlay">
                <div className="privacy-policy-modal">
                  <div className="modal-header">
                    <h2>End User Licence Agreement</h2>
                  </div>
                  <div className="modal-content" onScroll={handleScroll} ref={eulaRef}>
                    <div className="eula-section">
                      <h1>END USER LICENCE AGREEMENT</h1>
                      <p className="last-updated">Last updated March 25, 2025</p>
                      
                      <div className="eula-content">
                        <p>SnapGrade is licensed to You (End-User) by STEM@Home Ltd, located and registered at 71-75 Shelton Street, Covent Garden, London, W2 1RL, UK ('Licensor'), for use only under the terms of this Licence Agreement.</p>

                        <p>By downloading the Licensed Application from Apple's software distribution platform ('App Store'), Google's software distribution platform ('Play Store'), accessing the web application through a supported web browser, or any update thereto (as permitted by this Licence Agreement), You indicate that You agree to be bound by all of the terms and conditions of this Licence Agreement. App Store and Play Store are referred to in this Licence Agreement as 'Services'.</p>

                        <p>The parties of this Licence Agreement acknowledge that the Services are not a Party to this Licence Agreement and are not bound by any provisions or obligations with regard to the Licensed Application, such as warranty, liability, maintenance and support thereof. STEM@Home Ltd, not the Services, is solely responsible for the Licensed Application and the content thereof.</p>

                        <p>This Licence Agreement may not provide for usage rules for the Licensed Application that are in conflict with the latest Apple Media Services Terms and Conditions, Google Play Terms of Service, and standard web application usage terms ('Usage Rules'). STEM@Home Ltd acknowledges that it had the opportunity to review the Usage Rules and this Licence Agreement is not conflicting with them.</p>

                        <p>SnapGrade when purchased or downloaded through the Services, or accessed via web browser, is licensed to You for use only under the terms of this Licence Agreement. The Licensor reserves all rights not expressly granted to You. SnapGrade is to be used on:</p>
                        <ul>
                          <li>Devices that operate with Apple's operating systems ('iOS' and 'Mac OS')</li>
                          <li>Google's operating system ('Android')</li>
                          <li>Web browsers compatible with the web application version of SnapGrade</li>
                        </ul>

                        <h2>TABLE OF CONTENTS</h2>
                        <ol className="table-of-contents">
                          <li><a href="#section1">THE APPLICATION</a></li>
                          <li><a href="#section2">SCOPE OF LICENCE</a></li>
                          <li><a href="#section3">TECHNICAL REQUIREMENTS</a></li>
                          <li><a href="#section4">NO MAINTENANCE AND SUPPORT</a></li>
                          <li><a href="#section5">USE OF DATA</a></li>
                          <li><a href="#section6">USER-GENERATED CONTRIBUTIONS</a></li>
                          <li><a href="#section7">CONTRIBUTION LICENCE</a></li>
                          <li><a href="#section8">LIABILITY</a></li>
                          <li><a href="#section9">WARRANTY</a></li>
                          <li><a href="#section10">PRODUCT CLAIMS</a></li>
                          <li><a href="#section11">LEGAL COMPLIANCE</a></li>
                          <li><a href="#section12">CONTACT INFORMATION</a></li>
                          <li><a href="#section13">TERMINATION</a></li>
                          <li><a href="#section14">THIRD-PARTY TERMS OF AGREEMENTS AND BENEFICIARY</a></li>
                          <li><a href="#section15">INTELLECTUAL PROPERTY RIGHTS</a></li>
                          <li><a href="#section16">APPLICABLE LAW</a></li>
                          <li><a href="#section17">MISCELLANEOUS</a></li>
                        </ol>

                        <h2 id="section1">1. THE APPLICATION</h2>
                        <p>SnapGrade ('Licensed Application') is a piece of software created to Automatically marks work and gives diagnostic feedback, to reduce marking time. — and customised for iOS and Android mobile devices ('Devices'). It is used to reduce time spent marking and provide immediate diagnostic feedback to students.</p>
                        <p>The Licensed Application is not tailored to comply with industry-specific regulations (Health Insurance Portability and Accountability Act (HIPAA), Federal Information Security Management Act (FISA), etc.), so if your interactions would be subjected to such laws, you may not use this Licensed Application. You may not use the Licensed Application in a way that would violate the Gramm-Leach-Bliley Act (GLBA).</p>

                        <h2 id="section2">2. SCOPE OF LICENCE</h2>
                        <h3>2.1</h3>
                        <p>You are given a non-transferable, non-exclusive, non-sublicensable licence to:</p>
                        <ul>
                          <li>Install and use the Licensed Application on any Devices that You (End-User) own or control and as permitted by the Usage Rules, with the exception that such Licensed Application may be accessed and used by other accounts associated with You (End-User, The Purchaser) via Family Sharing or volume purchasing.</li>
                          <li>Access and use the web application version of SnapGrade through supported web browsers, subject to the same terms and conditions outlined in this Licence Agreement.</li>
                        </ul>

                        <h3>2.2</h3>
                        <p>This licence will also govern any updates of the Licensed Application provided by Licensor that replace, repair, and/or supplement the first Licensed Application, unless a separate licence is provided for such update, in which case the terms of that new licence will govern.</p>

                        <h3>2.3</h3>
                        <p>You may not share or make the Licensed Application available to third parties (unless to the degree allowed by the Usage Rules, and with STEM@Home Ltd's prior written consent), sell, rent, lend, lease or otherwise redistribute the Licensed Application.</p>

                        <h3>2.4</h3>
                        <p>You may not reverse engineer, translate, disassemble, integrate, decompile, remove, modify, combine, create derivative works or updates of, adapt, or attempt to derive the source code of the Licensed Application, or any part thereof (except with STEM@Home Ltd's prior written consent).</p>

                        <h3>2.5</h3>
                        <p>You may not copy (excluding when expressly authorised by this licence and the Usage Rules) or alter the Licensed Application or portions thereof. You may create and store copies only on devices that You own or control for backup keeping under the terms of this licence, the Usage Rules, and any other terms and conditions that apply to the device or software used. You must remove any intellectual property notices. You acknowledge that no unauthorized third parties may gain access to these copies at any time. If you sell your Devices to a third party, you must remove the Licensed Application from the Devices before doing so.</p>

                        <h3>2.6</h3>
                        <p>Violations of the obligations mentioned above, as well as the attempt of such infringement, may be subject to prosecution and damages.</p>

                        <h3>2.7</h3>
                        <p>Licensor reserves the right to modify the terms and conditions of licensing.</p>

                        <h3>2.8</h3>
                        <p>Nothing in this licence should be interpreted to restrict third-party terms. When using the Licensed Application, You must ensure that You comply with applicable third-party terms and conditions.</p>

                        <h2 id="section3">3. TECHNICAL REQUIREMENTS</h2>
                        <h3>3.1</h3>
                        <p>The Licensed Application requires:</p>
                        <ul>
                          <li>A firmware version 1.0.0 or higher for mobile applications</li>
                          <li>A modern web browser that supports the latest web standards for the web application version</li>
                        </ul>
                        <p>Licensor recommends using the latest version of firmware or browser.</p>

                        <h3>3.2</h3>
                        <p>Licensor attempts to keep the Licensed Application updated so that it complies with modified/new versions of the firmware and new hardware. You are not granted rights to claim such an update.</p>

                        <h3>3.3</h3>
                        <p>You acknowledge that it is Your responsibility to confirm and determine that the app and end-user device on which You intend to use the Licensed Application satisfies the technical specifications mentioned above.</p>

                        <h3>3.4</h3>
                        <p>Licensor reserves the right to modify the technical specifications as it sees appropriate at any time.</p>

                        <h2 id="section4">4. NO MAINTENANCE OR SUPPORT</h2>
                        <h3>4.1</h3>
                        <p>STEM@Home Ltd is not obligated, expressed or implied, to provide any maintenance, technical or other support for the Licensed Application.</p>

                        <h3>4.2</h3>
                        <p>STEM@Home Ltd and the End-User acknowledge that the Services have no obligation whatsoever to furnish any maintenance and support services with respect to the Licensed Application.</p>

                        <h2 id="section5">5. USE OF DATA</h2>
                        <p>You acknowledge that Licensor will be able to access and adjust Your downloaded Licensed Application content and Your personal information, and that Licensor's use of such material and information is subject to Your legal agreements with Licensor and Licensor's privacy policy: <a href="https://www.snapgrade.ai/privacy-policy" target="_blank" rel="noopener noreferrer">https://www.snapgrade.ai/privacy-policy</a>.</p>

                        <p>You acknowledge that the Licensor may periodically collect and use technical data and related information about your device, system, and application software, and peripherals, offer product support, facilitate the software updates, and for purposes of providing other services to you (if any) related to the Licensed Application. Licensor may also use this information to improve its products or to provide services or technologies to you, as long as it is in a form that does not personally identify you.</p>

                        <h2 id="section6">6. USER-GENERATED CONTRIBUTIONS</h2>
                        <p>The Licensed Application may invite you to chat, contribute to, or participate in blogs, message boards, online forums, and other functionality, and may provide you with the opportunity to create, submit, display, transmit, perform, publish, distribute, or broadcast content and materials to us or in the Licensed Application, including but not limited to text, writings, video, audio, photographs, graphics, comments, suggestions, or personal information or other material (collectively, 'Contributions'). Contributions may be viewable by other users of the Licensed Application and through third-party websites or applications. As such, any Contributions you transmit may be treated as non-confidential and non-proprietary. When you create or make available any Contributions, you thereby represent and warrant that:</p>

                        <ol>
                          <li>The creation, distribution, transmission, public display, or performance, and the accessing, downloading, or copying of your Contributions do not infringe the proprietary rights, including but not limited to the copyright, patent, trademark, trade secret, or moral rights of any third party.</li>
                          <li>You are the creator and owner of or have the necessary licences, rights, consents, releases, and permissions to use and to authorise use, the Licensed Application, and other users of the Licensed Application to use your Contributions in any manner contemplated by the Licensed Application and this Licence Agreement.</li>
                          <li>You have the written consent, release, and/or permission of each and every identifiable individual person in your Contributions to use the name or likeness of each and every such identifiable individual person to enable inclusion and use of your Contributions in any manner contemplated by the Licensed Application and this Licence Agreement.</li>
                          <li>Your Contributions are not false, inaccurate, misleading, or unauthorized advertising, promotional materials, pyramid schemes, chain letters, spam, mass mailings, or other forms of solicitation.</li>
                          <li>Your Contributions are not unsolicited or unauthorized advertising, promotional materials, pyramid schemes, chain letters, spam, mass mailings, or other forms of solicitation.</li>
                          <li>Your Contributions are not obscene, lewd, lascivious, filthy, violent, harassing, libelous, slanderous, or otherwise objectionable (as determined by us).</li>
                          <li>Your Contributions do not ridicule, mock, disparage, intimidate, or abuse anyone.</li>
                          <li>Your Contributions are not used to harass or threaten (in the legal sense of those terms) any other person and to promote violence against a specific person or class of people.</li>
                          <li>Your Contributions do not violate any applicable law, regulation, or rule.</li>
                          <li>Your Contributions do not violate the privacy rights of any third party.</li>
                          <li>Your Contributions do not violate any applicable law concerning child pornography, or otherwise intended to protect the health or well-being of minors.</li>
                          <li>Your Contributions do not include any offensive comments that are connected to race, national origin, gender, sexual preference, or physical handicap.</li>
                          <li>Your Contributions do not otherwise violate, or link to material that violates, any provision of this Licence Agreement, or any applicable law or regulation.</li>
                        </ol>

                        <p>Any use of the Licensed Application in violation of the foregoing violates this Licence Agreement and may result in, among other things, termination or suspension of your rights to use the Licensed Application.</p>

                        <h2 id="section7">7. CONTRIBUTION LICENCE</h2>
                        <p>By posting your Contributions to any part of the Licensed Application or making Contributions accessible to the Licensed Application by linking your account from the Licensed Application to any of your social networking accounts, you automatically grant, and you represent and warrant that you have the right to grant, to us an unrestricted, unlimited, irrevocable, perpetual, non-exclusive, transferable, royalty-free, fully-paid, worldwide right, and licence to host, use, copy, reproduce, disclose, sell, resell, publish, broad cast, retitle, archive, store, cache, publicly display, reformat, translate, transmit, excerpt (in whole or in part), and distribute such Contributions (including, without limitation, your image and voice) for any purpose, commercial advertising, or otherwise, and to prepare derivative works of, or incorporate in other works, such as Contributions, and grant and authorise sublicences of the foregoing. The use and distribution may occur in any media formats and through any media channels.</p>

                        <p>This licence will apply to any form, media, or technology now known or hereafter developed, and includes our use of your name, company and name, and franchise name, as applicable, and any of the trademarks, service marks, trade names, logos, and personal and commercial images you provide. You waive all moral rights of your Contributions and you warrant that moral rights have not otherwise been asserted in your Contributions.</p>

                        <p>We do not assert any ownership over your Contributions. You retain full ownership of all your Contributions and any intellectual property or other proprietary rights associated with your Contributions. We are not liable for any statements or representations in your Contributions provided by you in any area in the Licensed Application. You are solely responsible for your Contributions to the Licensed Application and you expressly agree to exonerate us from any and all liability to refrain from legal action regarding your Contributions.</p>

                        <p>We have the right, in our sole and absolute discretion, (1) to edit, redact, or otherwise change any Contributions; (2) to recategorise Contributions to place them in more appropriate locations in the Licensed Application; and (3) to prescreen or delete any Contributions at any time and for any reason, without notice. We have no obligation to monitor your Contributions.</p>

                        <h2 id="section8">8. LIABILITY</h2>
                        <h3>8.1</h3>
                        <p>Licensor's responsibility in the case of violation of obligations and tort shall be limited to intent and gross negligence. Only in case of a breach of essential contractual duties (cardinal obligations), Licensor shall also be liable in case of slight negligence. In any case, liability shall be limited to the foreseeable, contractually typical damages. The limitation mentioned above does not apply to injuries to life, limb, or health.</p>

                        <h3>8.2</h3>
                        <p>Licensor takes no accountability or responsibility for any damages caused due to a breach of duties according to Section 2 of this Licence Agreement. To avoid data loss, You are required to make use of backup functions of the Licensed Application to the extent allowed by applicable third-party terms and conditions of use. You are aware that in case of alterations or manipulations of the Licensed Application, You will not have access to the Licensed Application.</p>

                        <h2 id="section9">9. WARRANTY</h2>
                        <h3>9.1</h3>
                        <p>Licensor warrants that the Licensed Application is free of spyware, trojan horses, viruses, or any other malware at the time of Your download. Licensor warrants that the Licensed Application works as described in the user documentation.</p>

                        <h3>9.2</h3>
                        <p>No warranty is provided for the Licensed Application that is not executable on the device, that has been unauthorizedly modified, handled inappropriately or culpably, combined or installed with inappropriate hardware or software, used with inappropriate accessories, regardless of Your third parties, or if there are any other reasons outside STEM@Home Ltd's sphere of influence that affect the executability of the Licensed Application.</p>

                        <h3>9.3</h3>
                        <p>You are required to inspect the Licensed Application immediately after installing it and notify STEM@Home Ltd about issues discovered without delay by email provided in Contact Information. The defect report will be taken into consideration and further investigated if it has been emailed within a period of seven (7) days after discovery.</p>

                        <h3>9.4</h3>
                        <p>If we confirm that the Licensed Application is defective, STEM@Home Ltd reserves a choice to remedy the situation either by means of solving the defect or substitute delivery.</p>

                        <h3>9.5</h3>
                        <p>In the event of any failure of the Licensed Application to conform to any applicable warranty, You may notify the Services Store Operator, and Your Licensed Application purchase price will be refunded to You. To the maximum extent permitted by applicable law, the Services Store Operator will have no other warranty with respect to the Licensed Application, and any other losses, claims, damages, liabilities, expenses, and costs attributable to any negligence to adhere to any warranty.</p>

                        <h3>9.6</h3>
                        <p>If the user is an entrepreneur, any claim based on faults expires after a statutory period of limitation amounting to twelve (12) months after the Licensed Application was made available to the user. The statutory periods of limitation given by law apply for users who are consumers.</p>

                        <h2 id="section10">10. PRODUCT CLAIMS</h2>
                        <p>STEM@Home Ltd and the End-User acknowledge that STEM@Home Ltd, and not the Services, is responsible for addressing any claims of the End-User or any third party relating to the Licensed Application or the End-User's possession and/or use of that Licensed Application, including, but not limited to:</p>
                        <ol type="i">
                          <li>product liability claims,</li>
                          <li>any claim that the Licensed Application fails to conform to any applicable legal or regulatory requirement; and</li>
                          <li>claims arising under consumer protection, privacy, or similar legislation, including in connection with Your Licensed Application's use of the HealthKit and HomeKit.</li>
                        </ol>

                        <h2 id="section11">11. LEGAL COMPLIANCE</h2>
                        <p>You represent and warrant that You are not located in a country that is subject to a US Government embargo, or that has been designated by the US Government as a 'terrorist supporting' country, and that You are not listed on any US Government list of prohibited or restricted parties.</p>

                        <h2 id="section12">12. CONTACT INFORMATION</h2>
                        <p>For general inquiries, complaints, questions or claims concerning the Licensed Application, please contact:</p>
                        <address>
                          Francis Jones<br />
                          71-75 Shelton Street<br />
                          Covent Garden<br />
                          London, W2 1RL, UK<br />
                          <a href="mailto:francis.jones@snapgrade.ai">francis.jones@snapgrade.ai</a>
                        </address>

                        <h2 id="section13">13. TERMINATION</h2>
                        <p>The licence is valid until terminated by STEM@Home Ltd or by You. Your rights under this licence will terminate automatically and without notice from STEM@Home Ltd if You fail to adhere to any term(s) of this licence. Upon Licence termination, You shall stop all use of the Licensed Application, and destroy all copies, full or partial, of the Licensed Application.</p>

                        <h2 id="section14">14. THIRD-PARTY TERMS OF AGREEMENTS AND BENEFICIARY</h2>
                        <p>STEM@Home Ltd represents and warrants that STEM@Home Ltd will comply with applicable third-party terms of agreement when using Licensed Application.</p>

                        <p>In Accordance with Section 9 of the 'Instructions for Minimum Terms of Developer's End-User Licence Agreement', both Apple and Google and their subsidiaries shall be third-party beneficiaries of this End User Licence Agreement and — upon Your acceptance of the terms and conditions of this Licence Agreement, both Apple and Google will have the right (and will be deemed to have accepted the right) to enforce this End User Licence Agreement against You as a third-party beneficiary thereof.</p>

                        <h2 id="section15">15. INTELLECTUAL PROPERTY RIGHTS</h2>
                        <p>STEM@Home Ltd and the End-User acknowledge that, in the event of any third-party claim that the Licensed Application or the End-User's possession and use of that Licensed Application infringes on the third party's intellectual property rights, STEM@Home Ltd, and not the Services, will be solely responsible for the investigation, defence, settlement, and discharge of any such intellectual property infringement claims.</p>

                        <h2 id="section16">16. APPLICABLE LAW</h2>
                        <p>This Licence Agreement is governed by the laws of United Kingdom excluding its conflicts of law rules.</p>

                        <h2 id="section17">17. MISCELLANEOUS</h2>
                        <h3>17.1</h3>
                        <p>If any of the terms of this agreement should be or become invalid, the validity of the remaining provisions shall not be affected. Invalid terms will be replaced by valid ones formulated in a way that will achieve the primary purpose.</p>

                        <h3>17.2</h3>
                        <p>Collateral agreements, changes and amendments are only valid if laid down in writing. The preceding clause can only be waived in writing.</p>
                        
                        {/* Add spacer to ensure scrolling works properly */}
                        <div style={{ height: "120px", width: "100%" }}></div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-actions">
                    <button onClick={handlePrivacyPolicyDecline} className="btn-decline">
                      Decline
                    </button>
                    <button 
                      onClick={handleEULAAccept} 
                      className="btn-accept"
                      disabled={!canAccept}
                    >
                      Accept
                    </button>
                  </div>
                </div>
              </div>
            )}

            <button 
              type="submit" 
              className="create-account-button"
              disabled={isLoading || !!schoolCodeError || !formData.acceptedLicense || !!passwordError || !!confirmPasswordError || formData.password.length < 8 || formData.password !== formData.confirmPassword}
            >
              {isLoading ? "Creating Account..." : "Create Account"}
            </button>
          </form>

          <p className="login-prompt">
            Already have an account? <Link to="/login" className="login-link">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;